import { useTranslation } from 'react-i18next';
import { useNavigate } from '@tanstack/react-router';
import { useState, useMemo } from 'react';
import { useComments } from 'entities/comment';
import config from 'config';
import moment from 'moment';
import 'moment/locale/sv';

import { useEditorDocumentOptional } from 'app/api/documents/useEditorData';
import { useEditorLiveSection } from 'app/state/contexts/EditorUiContext';
import { useRouteParams } from 'app/router/params';

const CommentSelection = {
  ALL: { value: 'all', label: 'All' },
  DOCUMENT: { value: 'document', label: 'Document' },
  SECTION: { value: 'section', label: 'Section' },
};

interface NotificationMeta {
  documentSuperId?: number | string;
  author: string;
  organizationName?: string;
  documentName?: string;
  sectionName?: string;
  [key: string]: unknown;
}

interface CommentNotification {
  id: string;
  message?: string;
  createdAt?: string | Date;
  closedAt?: string | null;
  sectionSuperId?: string;
  meta: NotificationMeta;
  [key: string]: unknown;
}

interface NotificationsDrawerProps {
  onClose: () => void;
}

export const NotificationsDrawer = ({ onClose }: NotificationsDrawerProps) => {
  const { t } = useTranslation(['common', 'notifications']);

  const { superId: sectionSuperId } = useEditorLiveSection() as { superId: string };
  const { data: pDocument } = useEditorDocumentOptional();
  const { documentId } = useRouteParams();
  const documentSuperId = pDocument?.superId ?? (documentId ? Number(documentId) : undefined);
  const navigate = useNavigate();
  const { commentNotifications, isCommentNotificationsLoading } = useComments(sectionSuperId) as {
    commentNotifications: CommentNotification[] | undefined;
    isCommentNotificationsLoading: boolean;
  };
  const [commentSelection, setCommentSelection] = useState(CommentSelection.ALL);
  const filteredNotifications = useMemo(() => {
    switch (commentSelection.value) {
      case CommentSelection.ALL.value:
        return commentNotifications;
      case CommentSelection.DOCUMENT.value:
        return commentNotifications?.filter((c) => c.meta.documentSuperId === documentSuperId);
      case CommentSelection.SECTION.value:
        return commentNotifications?.filter((c) => c.sectionSuperId === sectionSuperId);
      default:
        return commentNotifications;
    }
  }, [commentNotifications, commentSelection.value, documentSuperId, sectionSuperId]);

  const closedComments = useMemo(() => filteredNotifications?.filter((c) => c.closedAt !== null), [filteredNotifications]);
  const openComments = useMemo(() => filteredNotifications?.filter((c) => c.closedAt === null), [filteredNotifications]);

  const handleNotificationClick = (notification: CommentNotification) => {
    const isSameSection = notification.sectionSuperId === sectionSuperId;
    onClose();
    navigate({
      to: '/documents/$documentId/sections/$sectionId',
      params: { documentId: String(notification.meta.documentSuperId), sectionId: String(notification.sectionSuperId) },
      search: isSameSection ? (prev: Record<string, unknown>) => ({ ...prev, goToCommentId: notification.id }) : { goToCommentId: notification.id },
      resetScroll: !isSameSection,
    });
  };

  const renderFilterButtons = () => {
    const isDocumentDisabled = !documentSuperId;
    const isSectionDisabled = !sectionSuperId;

    return (
      <>
        <button
          disabled={false}
          className={`drawer-filter-button ${commentSelection.value === CommentSelection.ALL.value && 'active'}`}
          onClick={() => setCommentSelection(CommentSelection.ALL)}
        >
          {CommentSelection.ALL.label}
        </button>
        <button
          disabled={isDocumentDisabled}
          className={`drawer-filter-button ${commentSelection.value === CommentSelection.DOCUMENT.value && 'active'}`}
          onClick={() => !isDocumentDisabled && setCommentSelection(CommentSelection.DOCUMENT)}
        >
          {CommentSelection.DOCUMENT.label}
        </button>
        <button
          disabled={isSectionDisabled}
          className={`drawer-filter-button ${commentSelection.value === CommentSelection.SECTION.value && 'active'}`}
          onClick={() => !isSectionDisabled && setCommentSelection(CommentSelection.SECTION)}
        >
          {CommentSelection.SECTION.label}
        </button>
      </>
    );
  };

  return (
    <div className="drawer-content">
      <div className="drawer-controls columns col-1fr-auto" onClick={onClose}>
        <i className="fa-regular fa-arrow-right-long drawer-arrow " />
      </div>
      <p className="drawer-title">{t('notifications:title')}</p>
      <div className="divider" />
      <div className="drawer-filters">{renderFilterButtons()}</div>

      {!isCommentNotificationsLoading && (
        <div className="notification-list">
          {(filteredNotifications?.length ?? 0) === 0 && (
            <div className="notification-list--empty">
              <i className="fa-regular fa-bell-slash" />
              {t('notifications:empty')}
            </div>
          )}
          {(openComments?.length ?? 0) > 0 && (
            <>
              <span>
                <b>{t('common:open')}</b>
                {` (${openComments?.length})`}
              </span>
              {openComments?.map((notification) => (
                <NotificationItem key={notification.id} notification={notification} locale={config.defaultLocale || 'sv'} onClick={handleNotificationClick} />
              ))}
            </>
          )}
          {(closedComments?.length ?? 0) > 0 && (
            <>
              <span>
                <b>{t('common:closed')}</b>
                {` (${closedComments?.length})`}
              </span>
              {closedComments?.map((notification) => (
                <NotificationItem key={notification.id} notification={notification} locale={config.defaultLocale || 'sv'} onClick={handleNotificationClick} />
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
};

const formatDate = (date: string | Date, locale: string) => {
  moment.locale(locale);
  return moment(new Date(date)).fromNow();
};

interface NotificationItemProps {
  notification: CommentNotification;
  locale: string;
  onClick: (notification: CommentNotification) => void;
}

const NotificationItem = ({ notification, locale, onClick }: NotificationItemProps) => {
  const { t } = useTranslation('notifications');

  return (
    <div key={notification.id} className="notification-item-wrapper" onClick={() => onClick(notification)}>
      <div className="user--circle">
        {notification.meta.author
          .split(' ')
          .map((w: string) => w.substr(0, 1))
          .map((c: string) => c.toUpperCase())
          .join('')}
      </div>
      <div>
        <span className="notification-item-text">
          <b>{`${notification.meta.author} `}</b>
          <span>
            {t('mentioned-text')} {notification.message}
          </span>
        </span>
        <div className="notification-item-info">
          <span>{formatDate(notification.createdAt ?? '', locale)} </span>
          <span className="notification-item-meta">{`${notification.meta.organizationName}, ${notification.meta.documentName}, ${notification.meta.sectionName}`}</span>
        </div>
      </div>
    </div>
  );
};
